import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import * as lodash from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, take, tap } from 'rxjs/operators';
import {
    BENEFITS_SERVICES_COLLECTION,
    COLLECTION_CONSULTANCY_PROCESS,
    COLLECTION_PRODUCTS,
    COLLECTION_STORE_CART,
    CONFIRM_COP_FINALIZATION,
    EMPLOYEES_COLLECTION,
    GET_PRODUCTS_FOR_EMPLOYEE_FUNCTION,
    RESET_CURRENT_STORE_CART,
} from '../../../../../app.constants';
import { FunctionOptions } from '../../../../../core/domain/function-options';
import { BaseService } from '../../../../../core/services/base.service';
import { UserService } from '../../../../../core/services/user.service';
import { Product, StoreCartProduct } from '../../../../../shared/models/product.model';
import { CopHomeService } from '../../cop-home.service';
import { FireLoggingService } from '../../../../../core/services/fire-logging.service';

@Injectable({
    providedIn: 'root',
})
export class CopStoreService extends BaseService {
    storeCart: any;
    deselectPerson: EventEmitter<number> = new EventEmitter<number>();
    selectProductColor: EventEmitter<any> = new EventEmitter<any>();
    selectProductCapacityStorage: EventEmitter<any> = new EventEmitter<any>();

    possibleToChooseSmartphone = true;

    constructor(
        protected override firestore: AngularFirestore,
        protected override http: HttpClient,
        protected override functions: AngularFireFunctions,
        protected userService: UserService,
        protected copHomeService: CopHomeService,
        protected override fireLogging: FireLoggingService
    ) {
        super(firestore, http, functions, fireLogging);
    }

    getProductsForEmployee() {
        return this.callFunction(
            this.userService.operatedByHelperUser ? {helperMetadata: {employeeId: this.userService.loggedUser.id, operatedByHelperUser: this.userService.operatedByHelperUser}} : null,
            GET_PRODUCTS_FOR_EMPLOYEE_FUNCTION,
            new FunctionOptions('callable')
        );
    }

    getCurrentStoreCart() {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.copHomeService.consultancyProcessGlobal.id)
            .collection(COLLECTION_STORE_CART)
            .get()
            .pipe(
                map((actions: any) =>
                    actions.docs.map((action: any) => {
                        const data: any = action.data();
                        data.id = action.id;
                        return data;
                    })
                )
            )
            .pipe(
                concatMap((data) => this.buildCurrentStoreCart(data)),
                catchError((error) => {
                    const message = error.error ? error.error.message : error.message;
                    const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                    ${COLLECTION_CONSULTANCY_PROCESS}/${this.copHomeService.consultancyProcessGlobal.id}/
                    ${COLLECTION_STORE_CART}`;
                    this.fireLogging.sendErrorLog(`[getCurrentStoreCart(cop-store.service.ts)] An error
                    occurred while getting item into collection ${path}, details: ${message || JSON.stringify(error)}`);
                    return of({ error });
                })
            );
    }

    updateSmartphoneList(smartPhoneList: any) {
        const result: any[] = [];
        this.storeCart.items.map((item: any) => {
            smartPhoneList.forEach((array: any) => {
                array.forEach((smartPhone: any) => {
                    if (item.product.id === smartPhone.id) {
                        item.product = smartPhone;
                        result.push(item);
                    }
                });
            });
        });
        return result;
    }

    updateStoreCart(data: StoreCartProduct | StoreCartProduct[]) {
        const newCurrentStoreArray = this.buildNewCopStoreArray(data);

        return new Observable((observer) => {
            newCurrentStoreArray.forEach((item: any) => {
                item.isASimpleProduct = item.product.isASimpleProduct;
                item.monthlyPayment = lodash.isNil(item.monthlyPayment)
                    ? item.product?.monthlyPayment
                    : item.monthlyPayment;
                item.financingFee = lodash.isNil(item.financingFee)
                    ? item.product?.financingFee
                    : item.financingFee;
                item.finsteinFee = lodash.isNil(item.finsteinFee)
                    ? item.product?.finsteinFee
                    : item.finsteinFee;
                item.netFinancedMonthlyPayment = lodash.isNil(
                    item.netFinancedMonthlyPayment
                )
                    ? item.product?.netFinancedMonthlyPayment
                    : item.netFinancedMonthlyPayment;
                item.privateFinancedMonthlyPayment = lodash.isNil(
                    item.privateFinancedMonthlyPayment
                )
                    ? item.product?.privateFinancedMonthlyPayment
                    : item.privateFinancedMonthlyPayment;
                item.newGrossSalary = lodash.isNil(item.newGrossSalary)
                    ? item.product?.newGrossSalary
                    : item.newGrossSalary;
                item.newNetSalary = lodash.isNil(item.newNetSalary)
                    ? item.product?.newNetSalary
                    : item.newNetSalary;
            });

            const selectedProducts = lodash.cloneDeep(newCurrentStoreArray);

            newCurrentStoreArray.forEach((item: any) => {
                item.product = this.firestore
                    .collection(COLLECTION_PRODUCTS)
                    .doc(item.product.id).ref;
            });

            this.firestore
                .collection(EMPLOYEES_COLLECTION)
                .doc(this.userService.loggedUser.id)
                .collection(COLLECTION_CONSULTANCY_PROCESS)
                .doc(this.copHomeService.consultancyProcessGlobal.id)
                .collection(COLLECTION_STORE_CART)
                .doc(this.storeCart.id)
                .set(
                    {
                        items: newCurrentStoreArray,
                        lastModifiedFrom: 'WEBSITE (updateStoreCart)',
                    },
                    { merge: true }
                )
                .then(() => {
                    this.storeCart = {
                        ...this.storeCart,
                        items: selectedProducts,
                    };
                    observer.next();
                })
                .catch((error) => {
                    const message = error.error ? error.error.message : error.message;
                    const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                    ${COLLECTION_CONSULTANCY_PROCESS}/${this.copHomeService.consultancyProcessGlobal.id}/
                    ${COLLECTION_STORE_CART}/${this.storeCart.id}`;
                    this.fireLogging.sendErrorLog(`[updateStoreCart(cop-store.service.ts)] An error occurred
                    while updating item into collection ${path}, details: ${message || JSON.stringify(error)}`);
                    observer.error();
                });
        });
    }

    removeItemFromStoreCart(itemToRemove: any) {
        const currentStoreCart = lodash.cloneDeep(this.storeCart.items);
        const indexToRemove = currentStoreCart.findIndex((item: any) => {
            return (
                item.person.uid === itemToRemove.person.uid &&
                item.product?.id === itemToRemove.smartphoneSelected?.id
            );
        });

        if (indexToRemove > -1) {
            this.storeCart.items.splice(indexToRemove, 1);
            currentStoreCart.splice(indexToRemove, 1);
            currentStoreCart.forEach((item: any) => {
                item.product = this.firestore
                    .collection(COLLECTION_PRODUCTS)
                    .doc(item.product.id).ref;
            });
        }

        return new Observable((observer) => {
            this.firestore
                .collection(EMPLOYEES_COLLECTION)
                .doc(this.userService.loggedUser.id)
                .collection(COLLECTION_CONSULTANCY_PROCESS)
                .doc(this.copHomeService.consultancyProcessGlobal.id)
                .collection(COLLECTION_STORE_CART)
                .doc(this.storeCart.id)
                .set(
                    {
                        items: currentStoreCart,
                        lastModifiedFrom: 'WEBSITE (removeItemFromStoreCart)',
                    },
                    { merge: true }
                )
                .then(() => observer.next())
                .catch((error) => {
                    const message = error.error ? error.error.message : error.message;
                    const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                    ${COLLECTION_CONSULTANCY_PROCESS}/${this.copHomeService.consultancyProcessGlobal.id}/
                    ${COLLECTION_STORE_CART}/${this.storeCart.id}`;
                    this.fireLogging.sendErrorLog(`[removeItemFromStoreCart(cop-store.service.ts)] An error
                    occurred while updating item into collection ${path},
                    details: ${message || JSON.stringify(error)}`);
                    observer.error();
                });
        });
    }

    /**
     * @description Reduces the currentStoreCart and newStoreItem to only one array removing their duplicates
     * @param currentStoreCart The storeCart that came from the database
     * @param newStoreItem The new item of the store-item addition
     * @returns A new array with the data merged beetwen persons and products
     */
    private buildNewCopStoreArray(data: StoreCartProduct | StoreCartProduct[]) {
        const newStoreItem: any = lodash.cloneDeep(data);
        const currentStoreCart = lodash.cloneDeep(this.storeCart.items);

        if (
            !Array.isArray(data) &&
            !newStoreItem &&
            currentStoreCart.length &&
            data.product['basicInfo'].id ===
            currentStoreCart[0].product['basicInfo'].id
        ) {
            return [];
        }

        if (Array.isArray(data) && newStoreItem) {
            return newStoreItem;
        }

        currentStoreCart.forEach((currentStoreCartItem: any, index: number) => {
            const indexToChange = currentStoreCart.findIndex(
                (item: any) => item.person.uid === newStoreItem.person.uid
            );

            if (indexToChange > -1) {
                currentStoreCart[indexToChange] = newStoreItem;
                this.storeCart.items[indexToChange] = Object.assign(
                    {},
                    currentStoreCart[indexToChange]
                );
                return;
            } else if (
                currentStoreCartItem.product.id === newStoreItem.product.id &&
                currentStoreCartItem.person.uid === newStoreItem.person.uid
            ) {
                currentStoreCart.splice(index, 1);
                currentStoreCart.push(newStoreItem);
                this.storeCart.items.push(Object.assign({}, newStoreItem));
                return;
            }
        });

        const personIndex = currentStoreCart.findIndex(
            (item: any) => item.person.uid === newStoreItem.person.uid
        );
        if (personIndex === -1) {
            currentStoreCart.push(newStoreItem);
            this.storeCart.items.push(Object.assign({}, newStoreItem));
        }

        return currentStoreCart;
    }

    confirmCopFinalization(): Observable<any> {
        return this.callFunction(
            this.userService.operatedByHelperUser ? {helperMetadata: {employeeId: this.userService.loggedUser.id, operatedByHelperUser: this.userService.operatedByHelperUser}} : null,
            CONFIRM_COP_FINALIZATION,
            new FunctionOptions('callable')
        );
    }

    async buildCurrentStoreCart(data: any) {
        const list: any[] = [];
        for (const item of data[0].items) {
            const product = await item.product
                .get()
                .then((snapshot: any) => snapshot.data());
            if (product) {
                product.id = item.product.id;
                const basicInfo = await product.basicInfo
                    .get()
                    .then((snapshot: any) => snapshot.data());
                const basicInfoId = product.basicInfo && product.basicInfo.id;
                delete item.product;
                list.push({
                    product: { ...basicInfo, basicInfoId, ...product },
                    ...item,
                });
            }
        }
        this.storeCart = { id: data[0].id, items: list };
        return this.storeCart;
    }

    getCopBenefitService(serviceId: string) {
        return this.firestore
            .collection(EMPLOYEES_COLLECTION)
            .doc(this.userService.loggedUser.id)
            .collection(COLLECTION_CONSULTANCY_PROCESS)
            .doc(this.copHomeService.consultancyProcessGlobal.id)
            .collection(BENEFITS_SERVICES_COLLECTION)
            .doc(serviceId)
            .get()
            .pipe(
                map((doc: any) => {
                    if (doc.exists) {
                        return {
                            id: doc.id,
                            ...doc.data(),
                        };
                    }
                }),
                catchError((error) => {
                    const message = error.error ? error.error.message : error.message;
                    const path = `${EMPLOYEES_COLLECTION}/${this.userService.loggedUser.id}/
                    ${COLLECTION_CONSULTANCY_PROCESS}/${this.copHomeService.consultancyProcessGlobal.id}/
                    ${BENEFITS_SERVICES_COLLECTION}/${serviceId}`;
                    this.fireLogging.sendErrorLog(`[getCopBenefitService(cop-store.service.ts)] An error
                    occurred while getting item into collection ${path}, details: ${message || JSON.stringify(error)}`);
                    return of({ error });
                })
            );
    }

    getNotSimpleProductsStoreCart(): any[] {
        if (this.storeCart && this.storeCart.items) {
            return this.storeCart.items.filter(
                (item: any) => !item.product.isASimpleProduct
            );
        }
        return [];
    }

    isAllSimpleProductsSelected(): false {
        if (!!this.storeCart?.items.length) {
            return this.storeCart.items.every(
                (item: any) => item.product.isASimpleProduct
            );
        }
        return false;
    }

    isStoreCartEmpty() {
        return this.storeCart?.items?.length === 0;
    }

    handlePersonSimpleProduct(smartphoneList: any) {
        const willReceiveSimpleSmartphone =
            this.copHomeService.getListOfPersonWillReceiveSimpleSmartphone();
        const simpleProduct = smartphoneList
            .reduce((acc: any, curr: any) => [ ...acc, ...curr ], [])
            .filter((product: Product) => product.isASimpleProduct)[0];

        let personSimpleProduct: any[] = [];
        if (simpleProduct) {
            personSimpleProduct = willReceiveSimpleSmartphone.map((person) => {
                return {
                    dependsOn:
                        person.uid !== this.userService.loggedUser.id
                            ? this.userService.loggedUser.id
                            : null,
                    simpleProductWasSelected: true,
                    person: person,
                    product: simpleProduct,
                };
            });
        }

        return personSimpleProduct;
    }

    resetCurrentStoreCart(): Observable<any> {
        return this.callFunction(
            this.userService.operatedByHelperUser ? {helperMetadata: {employeeId: this.userService.loggedUser.id, operatedByHelperUser: true}} : null,
            RESET_CURRENT_STORE_CART,
            new FunctionOptions('callable')
        ).pipe(
            switchMap(() => this.getCurrentStoreCart()),
            take(1),
        );
    }

    hasAvailableProductsToChoose() {
        return this.getProductsForEmployee()
            .pipe(
                tap(
                    (resp) => {
                        if (Array.isArray(resp)) {
                            if (resp.length <= 1) {
                                this.possibleToChooseSmartphone = false;
                            } else {
                                const availableProducts = resp.filter(
                                    (products: any[]) => {
                                        const notSimpleProducts = products
                                            .filter((product) => !product.isASimpleProduct);
                                        if (notSimpleProducts.length) {
                                            return notSimpleProducts;
                                        }
                                        return null;
                                    }
                                );

                                this.possibleToChooseSmartphone =
                                    !!availableProducts.length;
                            }
                        }
                    }
                )
            );
    }
}
